import { apiDefault, coreApi, screeningsApiClient } from 'src/api/config';
import { get } from 'lodash';
import Notifier from 'common/helpers/Notifier';
import { canFetchActionables, isReferralUser } from 'common/utils/Roles/isOrgAdmin';
import {
  DASHBOARD_FETCH_ACTIONABLES,
  DASHBOARD_SET_ACTIONABLES_IS_FETCHING,
  DASHBOARD_UNSET_ACTIONABLES_IS_FETCHING,
} from 'actions';
import {
  crtb1855ReferralsActionablesAggregatedCounts,
  scrn7ScreeningsCompletion,
  scrn655inboundScreeningsDashboard,
} from 'common/utils/FeatureFlags/flags';

function onSuccess(response, dispatch, updated) {
  dispatch({
    type: DASHBOARD_FETCH_ACTIONABLES,
    payload: response,
    updated,
  });

  dispatch({ type: DASHBOARD_UNSET_ACTIONABLES_IS_FETCHING });
  return response;
}

function onError(dispatch, error) {
  dispatch({ type: DASHBOARD_UNSET_ACTIONABLES_IS_FETCHING });
  Notifier.handleErrors(error);
  return error;
}

const mergeExternalScreeningsCount = (counts, count) => ({
  ...counts,
  screenings: {
    external: count,
  },
});

const isActionablesCountsPayload = (value) => (
  Boolean(value) &&
  typeof value === 'object' &&
  (
    Boolean(value.referrals) ||
    Boolean(value.assistance_requests) ||
    Boolean(value.contacts) ||
    value.assistanceRequestForms !== undefined ||
    Boolean(value.screenings)
  )
);

const extractActionablesCounts = (response) => {
  const candidates = [
    get(response, 'data.data.attributes'),
    get(response, 'data.data'),
    get(response, 'data.attributes'),
    get(response, 'data'),
  ];

  return candidates.find(isActionablesCountsPayload) || null;
};

const fetchLegacyActionablesCounts = async ({
  groupId,
  canFetchAllActionables,
  canFetchOutboundActionables,
}) => {
  const pageOptions = { page: { number: 1, size: 1 } };
  const outbboundReferralsQueries = [
    coreApi.query('referral', {
      state: 'recalled',
      sending_provider: groupId,
      'case.has_outcome': false,
      flagged_for_sender: true,
    }, pageOptions),

    coreApi.query('referral', {
      state: 'declined,denied_authorization',
      sending_provider: groupId,
      'case.has_outcome': false,
      flagged_for_sender: true,
    }, pageOptions),

    coreApi.query('referral', {
      state: 'sent',
      sending_provider: groupId,
      'case.person.has_consent': false,
    }, pageOptions),

    coreApi.query('referral', {
      state: 'sent',
      sending_provider: groupId,
      'case.person.consent.state': 'accepted',
    }, pageOptions),

    coreApi.query('case', {
      state: 'draft',
      has_outcome: false,
      include_pathways: false,
      provider: groupId,
    }, pageOptions),
  ];

  if (canFetchAllActionables) {
    const [
      assistanceRequests,
      referralsReceived,
      receivedReferralsPendingConsent,
      clientsRequiringAction,
      referralsInReview,
      referralsRecalled,
      referralsRejected,
      sentReferralsPendingConsent,
      awaitingResponseReferrals,
      referralsDraft,
      assistanceRequestForms,
    ] = await Promise.all([
      coreApi.query('assistance_request', {
        originating_provider: groupId,
      }, pageOptions),

      coreApi.query('referral', {
        state: 'sent',
        'case.has_outcome': false,
        receiving_provider: groupId,
      }, pageOptions),

      coreApi.query('referral', {
        state: 'sent',
        receiving_provider: groupId,
        'case.person.has_consent': false,
      }, pageOptions),

      coreApi.query('person', {
        'client_relationships.flagged': true,
        'client_relationships.provider': groupId,
      }, pageOptions),

      coreApi.query('referral', {
        state: 'in_review',
        'case.has_outcome': false,
        receiving_provider: groupId,
        'case.person.consent.state': 'accepted',
      }, pageOptions),

      ...outbboundReferralsQueries,

      coreApi.query('assistance_request_config', {
        provider: groupId,
      }, pageOptions),
    ]);

    return {
      referrals: {
        received: {
          pending_consent: get(receivedReferralsPendingConsent, 'data.meta.page.total_count', 0),
          pending: get(referralsReceived, 'data.meta.page.total_count', 0),
          in_review: get(referralsInReview, 'data.meta.page.total_count', 0),
        },
        sent: {
          pending_consent: get(sentReferralsPendingConsent, 'data.meta.page.total_count', 0),
          awaiting_response: get(awaitingResponseReferrals, 'data.meta.page.total_count', 0),
          rejected: get(referralsRejected, 'data.meta.page.total_count', 0),
          recalled: get(referralsRecalled, 'data.meta.page.total_count', 0),
          draft: get(referralsDraft, 'data.meta.page.total_count', 0),
        },
      },
      assistance_requests: {
        pending: get(assistanceRequests, 'data.meta.page.total_count', 0),
      },
      contacts: { flagged: get(clientsRequiringAction, 'data.meta.page.total_count', 0) },
      assistanceRequestForms: get(assistanceRequestForms, 'data.data.length', 0),
    };
  }

  if (canFetchOutboundActionables) {
    const [
      referralsRecalled,
      referralsRejected,
      sentReferralsPendingConsent,
      awaitingResponseReferrals,
      referralsDraft,
    ] = await Promise.all(outbboundReferralsQueries);

    return {
      referrals: {
        sent: {
          pending_consent: get(sentReferralsPendingConsent, 'data.meta.page.total_count', 0),
          awaiting_response: get(awaitingResponseReferrals, 'data.meta.page.total_count', 0),
          rejected: get(referralsRejected, 'data.meta.page.total_count', 0),
          recalled: get(referralsRecalled, 'data.meta.page.total_count', 0),
          draft: get(referralsDraft, 'data.meta.page.total_count', 0),
        },
      },
    };
  }

  return {};
};

const fetchAggregatedActionablesCounts = async (groupId) => {
  const countsResponse = await apiDefault.get('/referrals/actionables_counts', {
    params: {
      filter: {
        provider: groupId,
      },
    },
  });

  const countsData = extractActionablesCounts(countsResponse);
  if (!countsData) {
    throw new Error('Invalid actionables_counts response payload');
  }

  return countsData;
};

const fetchActionables = (groupId) => (
  async (dispatch, getState) => {
    try {
      const state = getState();
      const loadExtScreenings = scrn7ScreeningsCompletion(state) && scrn655inboundScreeningsDashboard(state);
      const useAggregatedActionablesCounts = crtb1855ReferralsActionablesAggregatedCounts(state);
      const userRoles = get(state, 'globalState.currentEmployee.roles', []);
      const allRoles = get(state, 'globalState.roles');
      const canFetchAllActionables = canFetchActionables(userRoles, allRoles);
      const canFetchOutboundActionables = isReferralUser(userRoles, allRoles);

      if (!canFetchAllActionables && !canFetchOutboundActionables) { return false; }

      dispatch({ type: DASHBOARD_SET_ACTIONABLES_IS_FETCHING });

      let countsData = useAggregatedActionablesCounts ?
        await fetchAggregatedActionablesCounts(groupId) :
        await fetchLegacyActionablesCounts({
          groupId,
          canFetchAllActionables,
          canFetchOutboundActionables,
        });

      if (canFetchAllActionables) {
        let externalScreeningsCount = 0;

        if (loadExtScreenings) {
          const externalScreenings = await screeningsApiClient.get('/screenings', {
            params: {
              type: 'screening',
              status: 'complete',
              source: 'external,self_screen',
              is_assigned: false,
              client_relationship: groupId,
            },
          });
          externalScreeningsCount = get(externalScreenings, 'data.total', 0);
        }

        countsData = mergeExternalScreeningsCount(countsData, externalScreeningsCount);
      }

      const updated = Date.now();
      const countsPayload = {
        data: {
          data: countsData,
        },
      };
      return onSuccess(countsPayload, dispatch, updated);
    } catch (error) {
      return onError(dispatch, error);
    }
  }
);

export default fetchActionables;
